<template>
	<v-container fluid class="kanban-view px-0 pt-0">

		<!-- Banner informativo -->
		<v-alert
			v-if="showInfo"
			type="info"
			outlined
			dense
			dismissible
			class="mb-3 mt-2"
			style="font-size:12px;"
			@input="dismissInfo"
		>
			<p class="mb-1 font-weight-bold" style="font-size:13px;">Uso del tablero Kanban</p>
			<ul class="mb-0 pl-4">
				<li>Puedes arrastrar tareas entre columnas para organizar visualmente su avance.</li>
				<li>Sólo puedes mover tareas donde eres <strong>creador o responsable</strong>.</li>
				<li>El <strong>Backlog</strong> contiene tus tareas aún no asignadas a ninguna columna. Arrástralas hacia una columna para incorporarlas al flujo.</li>
				<li>Activa <em>"Ver todas las tareas"</em> para visualizar tareas de otros usuarios (sólo lectura).</li>
				<li>El movimiento entre columnas <strong>NO modifica el estado interno</strong> de la tarea ni ejecuta cambios de negocio.</li>
				<li>Para cambiar el estado real de una tarea utiliza los mecanismos definidos en la aplicación.</li>
			</ul>
		</v-alert>

		<!-- Toolbar -->
		<div class="d-flex align-center flex-wrap mt-3 mb-3" style="gap:8px; padding-top:6px;">
			<v-spacer />
			<div class="d-flex align-center ver-todas-container" style="align-items:center;">
				<v-switch
					v-model="showAllTasks"
					dense
					hide-details
					color="#2D4B95"
					class="pa-0 ma-0"
					@change="loadKanban"
				/>
				<span class="caption ml-2 mr-3" style="font-weight:500;color:#2D4B95;">Ver todas</span>
			</div>
			<v-divider vertical class="mx-1" style="height:24px;align-self:center;" />
			<v-btn
				v-if="hasColumnPermission"
				small
				color="#2D4B95"
				dark
				elevation="0"
				@click="openCreateColumn"
			>
				<v-icon small class="mr-1">mdi-plus</v-icon>
				Agregar columna
			</v-btn>
			<v-tooltip bottom>
				<template v-slot:activator="{ on, attrs }">
					<v-btn icon small @click="loadKanban" :loading="loading" v-bind="attrs" v-on="on">
						<v-icon small color="grey darken-1">mdi-refresh</v-icon>
					</v-btn>
				</template>
				<span>Recargar tablero</span>
			</v-tooltip>
			<v-tooltip bottom v-if="!showInfo">
				<template v-slot:activator="{ on, attrs }">
					<v-btn icon small @click="showInfo = true" v-bind="attrs" v-on="on">
						<v-icon small color="#2D4B95">mdi-information-outline</v-icon>
					</v-btn>
				</template>
				<span>Ver instrucciones del tablero</span>
			</v-tooltip>
		</div>

		<!-- Loader -->
		<div v-if="loading" class="d-flex justify-center align-center" style="min-height:300px;">
			<div class="text-center">
				<v-progress-circular indeterminate color="#2D4B95" size="48"></v-progress-circular>
				<p class="mt-3 caption grey--text">Cargando tablero Kanban...</p>
			</div>
		</div>

		<!-- Empty state: sin columnas -->
		<div v-else-if="!loading && columns.length === 0" class="d-flex justify-center align-center" style="min-height:300px;">
			<div class="text-center">
				<template v-if="hasColumnPermission">
					<v-icon size="64" color="grey lighten-2">mdi-view-column-outline</v-icon>
					<p class="mt-3 mb-1 font-weight-medium grey--text">No hay columnas configuradas para este proyecto.</p>
					<p class="caption grey--text mb-3">Crea la primera columna para comenzar a organizar el flujo de trabajo.</p>
					<v-btn color="#2D4B95" dark small @click="openCreateColumn">
						<v-icon small class="mr-1">mdi-plus</v-icon>
						Crear primera columna
					</v-btn>
				</template>
				<template v-else>
					<v-icon size="64" color="grey lighten-2">mdi-lock-outline</v-icon>
					<p class="mt-3 mb-1 font-weight-medium grey--text">No hay columnas configuradas para este proyecto.</p>
					<p class="caption grey--text">Un usuario con el permiso <strong>set_kanban_project_column</strong> debe crear las columnas del tablero.</p>
				</template>
			</div>
		</div>

		<!-- Board -->
		<div v-else ref="board" class="kanban-board">
			<!-- Columna Backlog siempre a la izquierda -->
			<KanbanColumn
				:column="backlogMeta"
				:tasks="backlogTasksFiltered"
				:isBacklog="true"
				:hasColumnPermission="hasColumnPermission"
				:currentUserId="currentUserId"
				:scrollContainer="boardEl"
				@drag-start="onDragStart"
				@drag-end="onDragEnd"
				@search-change="backlogSearch = $event"
				@open-task="openTask"
			/>

			<!-- Columnas reales (reordenables) -->
			<draggable
				:list="columns"
				:group="{ name: 'kanban-columns' }"
				handle=".col-drag-handle"
				:animation="200"
				ghost-class="column-ghost"
				style="display:contents"
				:options="columnDraggableOptions"
				@end="onColumnDragEnd"
			>
				<KanbanColumn
					v-for="col in columns"
					:key="col.id"
					:column="col"
					:tasks="col.tasks"
					:isBacklog="false"
					:hasColumnPermission="hasColumnPermission"
					:currentUserId="currentUserId"
					:scrollContainer="boardEl"
					@drag-start="onDragStart"
					@drag-end="onDragEnd"
					@edit-column="openEditColumn"
					@delete-column="confirmDeleteColumn"
					@open-task="openTask"
				/>
			</draggable>
		</div>

		<!-- Modal gestión de columnas -->
		<KanbanColumnModal
			:show="showColumnModal"
			:column="editingColumn"
			:nextPosition="columns.length + 1"
			:projectId="projectId"
			@close="showColumnModal = false"
			@saved="onColumnSaved"
			@deleted="onColumnDeleted"
		/>

		<!-- Confirm delete dialog -->
		<v-dialog v-model="showDeleteConfirm" max-width="380">
			<v-card>
				<v-card-title style="font-size:15px;color:#2D4B95;">Eliminar columna</v-card-title>
				<v-card-text>
					¿Estás seguro que deseas eliminar la columna <strong>{{ columnToDelete ? columnToDelete.name : '' }}</strong>?
					Las tareas asignadas a esta columna quedarán sin columna asignada.
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text small @click="showDeleteConfirm = false" :disabled="deletingColumn">Cancelar</v-btn>
					<v-btn text small color="red" @click="executeDeleteColumn" :loading="deletingColumn" :disabled="deletingColumn">Eliminar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

	</v-container>
</template>

<script>
import draggable from 'vuedraggable'
import { getProjectKanban, setTaskColumn, updateProjectColumnsPosition, deleteProjectColumn } from '@/helpers/api/kanban'
import { listMyTasks, getTareaByID } from '@/helpers/api/tasks'
import { checkPermissions } from '@/helpers/permissions'
import EventBus from '@/helpers/event_bus'
import KanbanColumn from './KanbanColumn.vue'
import KanbanColumnModal from './KanbanColumnModal.vue'

export default {
	name: 'KanbanView',
	components: { draggable, KanbanColumn, KanbanColumnModal },
	data: () => ({
		loading: false,
		showAllTasks: false,
		showInfo: localStorage.getItem('kanban_info_dismissed') !== 'true',
		columns: [],
		backlogTasks: [],
		columnsSnapshot: [],
		backlogSnapshot: [],
		draggedItem: null,
		draggedFromColumn: null,
		showColumnModal: false,
		editingColumn: null,
		showDeleteConfirm: false,
		columnToDelete: null,
		deletingColumn: false,
		backlogMeta: {
			id: '__backlog__',
			name: 'Backlog',
			color: '#607D8B'
		},
		backlogSearch: '',
		boardEl: null
	}),
	computed: {
		currentUserId() {
			return this.$store.userStore.state.userInfo?.id || null
		},
		projectId() {
			const project = this.$store.projectsStore.state.selectedProject
			if (project) return project.id
			const stored = localStorage.getItem('selectedProjectID')
			return stored ? parseInt(stored) : null
		},
		hasColumnPermission() {
			return checkPermissions(['set_kanban_project_column'])
		},
		backlogTasksFiltered() {
			let tasks = this.backlogTasks
			if (this.backlogSearch && this.backlogSearch.trim()) {
				const q = this.backlogSearch.toLowerCase()
				tasks = tasks.filter(t =>
					(t.task.title && t.task.title.toLowerCase().includes(q)) ||
					(t.task.number && String(t.task.number).includes(q))
				)
				return tasks
			}
			return tasks.slice(0, 50)
		},
		columnDraggableOptions() {
			return {
				scroll: this.boardEl || true,
				scrollSensitivity: 120,
				scrollSpeed: 20
			}
		}
	},
	mounted() {
		EventBus.$on('kanbanTaskColumnChanged', this.onTaskColumnChanged)
		if (this.projectId) {
			this.loadKanban()
		}
		this.$nextTick(() => {
			this.boardEl = this.$refs.board || null
		})
	},
	beforeDestroy() {
		EventBus.$off('kanbanTaskColumnChanged', this.onTaskColumnChanged)
	},
	methods: {
		async loadKanban() {
			if (!this.projectId) return
			this.loading = true
			try {
				const [kanbanResp, myTasksResp] = await Promise.all([
					getProjectKanban({
						project_id: this.projectId,
						show_all_tasks: this.showAllTasks
					}),
					listMyTasks({
						project_id: this.projectId,
						status: 1,
						start: 0,
						limit: 200
					})
				])

				if (kanbanResp.code === 200) {
					// Normalizar tasks de cada columna para tener acceso a task.responsable
					this.columns = (kanbanResp.columns || []).map(col => ({
						...col,
						tasks: (col.tasks || []).map(t => ({
							...t,
							// Asegurar que task.responsable está disponible desde el objeto task
							task: t.task || {}
						}))
					}))
					this.$store.kanbanStore.commit('setProjectColumns', {
						projectId: this.projectId,
						columns: this.columns.map(({ id, name, color, position, project_id }) => ({ id, name, color, position, project_id }))
					})
				}

				// Construir Backlog: tareas del usuario no asignadas a ninguna columna real
				const assignedTaskIds = new Set()
				this.columns.forEach(col => {
					col.tasks.forEach(t => assignedTaskIds.add(t.task_id))
				})

				let myTasks = []
				if (myTasksResp.code === 200) {
					const created = myTasksResp.my_created_tasks || []
					const pending = myTasksResp.my_tasks || []
					// Deduplicar por id
					const seen = new Set()
					;[...created, ...pending].forEach(t => {
						if (!seen.has(t.id)) {
							seen.add(t.id)
							myTasks.push(t)
						}
					})
				}

				this.backlogTasks = myTasks
					.filter(t => !assignedTaskIds.has(t.id))
					.map(t => ({
						_backlog: true,
						task_id: t.id,
						task: t
					}))

			} catch (e) {
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al cargar el tablero Kanban.',
					title: 'Error',
					created: new Date(),
					type: 'danger',
					icon: 'mdi-close-octagon',
					color: '#F4516C'
				})
			} finally {
				this.loading = false
			}
		},

		dismissInfo() {
			this.showInfo = false
			localStorage.setItem('kanban_info_dismissed', 'true')
		},

		onTaskColumnChanged(payload) {
			if (payload && Number(payload.project_id) !== Number(this.projectId)) return
			this.loadKanban()
		},

		// Auto-scroll horizontal durante drag
		// (manejado nativamente por SortableJS via :scroll prop en KanbanColumn)

		// Snapshot profundo antes del drag
		onDragStart() {
			this.columnsSnapshot = JSON.parse(JSON.stringify(this.columns))
			this.backlogSnapshot = JSON.parse(JSON.stringify(this.backlogTasks))
		},

		async onDragEnd({ evt, column } = {}) {
			// Reordenamiento dentro de la misma columna
			if (evt && evt.from === evt.to) {
				if (evt.oldIndex === evt.newIndex) return
				// Verificar permiso sobre la tarea reordenada
				const targetCol = this.columns.find(c => c.id === column?.id)
				const movedTask = targetCol?.tasks[evt.newIndex]
				if (movedTask) {
					const canMove = movedTask.task.created_by === this.currentUserId ||
						movedTask.task.responsable === this.currentUserId
					if (!canMove) {
						this.revertDrag()
						this.$store.globalStore.dispatch('addMessage', {
							message: 'No tienes permiso para reordenar esta tarea.',
							title: 'Acción no permitida',
							created: new Date(),
							type: 'warning',
							icon: 'mdi-alert-outline',
							color: '#fb8c00'
						})
						return
					}
				}
				try {
					const columnsPayload = {}
					this.columns.forEach(col => {
						columnsPayload[col.id] = col.tasks.map(t => t.task_id)
					})
					await setTaskColumn({ columns: columnsPayload })
				} catch (e) {
					this.revertDrag()
					this.$store.globalStore.dispatch('addMessage', {
						message: 'Error al reordenar la tarea. Por favor intenta nuevamente.',
						title: 'Error',
						created: new Date(),
						type: 'danger',
						icon: 'mdi-close-octagon',
						color: '#F4516C'
					})
				}
				return
			}

			// Movimiento entre columnas
			// vuedraggable ya actualizó los arrays reactivamente.
			// Buscar la tarea movida comparando el estado actual con el snapshot.
			const movedItem = this.findMovedItem()
			if (!movedItem) {
				// No se pudo identificar la tarea, revertir
				this.revertDrag()
				return
			}

			// Verificar permisos
			const canMove = movedItem.task.created_by === this.currentUserId ||
				movedItem.task.responsable === this.currentUserId

			if (!canMove) {
				this.revertDrag()
				this.$store.globalStore.dispatch('addMessage', {
					message: 'No tienes permiso para mover esta tarea. Solo puedes mover tareas donde eres creador o responsable.',
					title: 'Acción no permitida',
					created: new Date(),
					type: 'warning',
					icon: 'mdi-alert-outline',
					color: '#fb8c00'
				})
				return
			}

			// Identificar la columna destino
			const targetColumn = this.findTargetColumn(movedItem)
			if (!targetColumn) {
				// Si el destino no es una columna real (backlog no acepta drops), revertir
				this.revertDrag()
				return
			}

			// Llamada API
			try {
				const columnsPayload = {}
				this.columns.forEach(col => {
					columnsPayload[col.id] = col.tasks.map(t => t.task_id)
				})
				await setTaskColumn({ columns: columnsPayload })
				this.$store.globalStore.dispatch('addMessage', {
					message: `Tarea movida a "${targetColumn.name}" correctamente.`,
					title: 'Tarea actualizada',
					created: new Date(),
					type: 'success',
					icon: 'mdi-check-circle-outline',
					color: '#34BFA3'
				})
				// Si vino del backlog, removerla del array fuente
				if (movedItem._backlog) {
					this.backlogTasks = this.backlogTasks.filter(t => t.task_id !== movedItem.task_id)
				}
			} catch (e) {
				this.revertDrag()
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al mover la tarea. Por favor intenta nuevamente.',
					title: 'Error',
					created: new Date(),
					type: 'danger',
					icon: 'mdi-close-octagon',
					color: '#F4516C'
				})
			}
		},

		findMovedItem() {
			// Comparar el estado actual con el snapshot para encontrar el item movido.
			// Buscamos en todas las columnas reales el item que no estaba antes en esa columna.
			for (const col of this.columns) {
				const snapCol = this.columnsSnapshot.find(c => c.id === col.id)
				const snapIds = new Set((snapCol?.tasks || []).map(t => t.task_id))
				for (const item of col.tasks) {
					if (!snapIds.has(item.task_id)) {
						return item
					}
				}
			}
			return null
		},

		findTargetColumn(movedItem) {
			for (const col of this.columns) {
				const found = col.tasks.find(t => t.task_id === movedItem.task_id)
				if (found) return col
			}
			return null
		},

		revertDrag() {
			// Restaurar arrays desde snapshots
			this.columns = this.columns.map(col => {
				const snap = this.columnsSnapshot.find(c => c.id === col.id)
				return snap ? { ...col, tasks: snap.tasks } : col
			})
			this.backlogTasks = [...this.backlogSnapshot]
		},

		async onColumnDragEnd() {
			// vuedraggable ya reordenó this.columns. Guardar posiciones en la API.
			try {
				await updateProjectColumnsPosition({
					columns: this.columns.map((col, idx) => ({
						column_id: col.id,
						position: idx + 1
					}))
				})
				this.$store.kanbanStore.commit('setProjectColumnsPosition', {
					projectId: this.projectId,
					columns: this.columns.map((col, idx) => ({ id: col.id, position: idx + 1 }))
				})
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Orden de columnas guardado correctamente.',
					title: 'Columnas reorganizadas',
					created: new Date(),
					type: 'success',
					icon: 'mdi-check-circle-outline',
					color: '#34BFA3'
				})
			} catch (e) {
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al guardar el orden de columnas.',
					title: 'Error',
					created: new Date(),
					type: 'danger',
					icon: 'mdi-close-octagon',
					color: '#F4516C'
				})
				this.loadKanban()
			}
		},

		// Gestión de columnas
		openCreateColumn() {
			this.editingColumn = null
			this.showColumnModal = true
		},
		openEditColumn(col) {
			this.editingColumn = col
			this.showColumnModal = true
		},
		onColumnSaved(savedColumn) {
			this.showColumnModal = false
			if (this.editingColumn) {
				// Actualizar columna existente
				const colIdx = this.columns.findIndex(c => c.id === savedColumn.id)
				if (colIdx !== -1) {
					this.$set(this.columns, colIdx, { ...this.columns[colIdx], ...savedColumn })
				}
				this.$store.kanbanStore.commit('upsertProjectColumn', savedColumn)
				this.$store.globalStore.dispatch('addMessage', {
					message: `Columna "${savedColumn.name}" actualizada.`,
					title: 'Columna actualizada',
					created: new Date(),
					type: 'success',
					icon: 'mdi-check-circle-outline',
					color: '#34BFA3'
				})
			} else {
				// Agregar nueva columna
				this.columns.push({ ...savedColumn, tasks: [] })
				this.$store.kanbanStore.commit('upsertProjectColumn', savedColumn)
				this.$store.globalStore.dispatch('addMessage', {
					message: `Columna "${savedColumn.name}" creada.`,
					title: 'Columna creada',
					created: new Date(),
					type: 'success',
					icon: 'mdi-check-circle-outline',
					color: '#34BFA3'
				})
			}
		},
		confirmDeleteColumn(col) {
			this.columnToDelete = col
			this.showDeleteConfirm = true
		},
		async executeDeleteColumn() {
			if (!this.columnToDelete) return
			this.showDeleteConfirm = false
			this.deletingColumn = true
			this.loading = true
			// Snapshots to revert in case of error
			const prevColumns = this.columns.slice()
			const prevBacklog = this.backlogTasks.slice()
			const colId = this.columnToDelete.id
			try {
				// Mover tareas de la columna al backlog (solo localmente para UX)
				const col = this.columns.find(c => c.id === colId)
				if (col && col.tasks.length > 0) {
					const toBacklog = col.tasks.map(t => ({
						_backlog: true,
						task_id: t.task_id,
						task: t.task
					}))
					toBacklog.forEach(item => {
						const mine = item.task.created_by === this.currentUserId ||
							item.task.responsable === this.currentUserId
						if (mine) this.backlogTasks.push(item)
					})
				}
				// Optimistic UI: remover columna localmente
				this.columns = this.columns.filter(c => c.id !== colId)
				// Llamada a la API para eliminar en servidor
				const resp = await deleteProjectColumn({ column_id: colId })
				if (resp && resp.code === 200) {
					this.$store.kanbanStore.commit('removeProjectColumn', { projectId: this.projectId, columnId: colId })
					this.$store.globalStore.dispatch('addMessage', {
						message: `Columna "${this.columnToDelete.name}" eliminada.`,
						title: 'Columna eliminada',
						created: new Date(),
						type: 'success',
						icon: 'mdi-check-circle-outline',
						color: '#34BFA3'
					})
					this.columnToDelete = null
					// Refrescar desde servidor para asegurar consistencia
					this.loadKanban()
				} else {
					this.loading = false
					throw new Error(resp && resp.message ? resp.message : 'Error al eliminar columna')
				}
			} catch (e) {
				// Revertir cambios locales
				this.loading = false
				this.columns = prevColumns
				this.backlogTasks = prevBacklog
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al eliminar la columna. Por favor intenta nuevamente.',
					title: 'Error',
					created: new Date(),
					type: 'danger',
					icon: 'mdi-close-octagon',
					color: '#F4516C'
				})
			} finally {
				this.deletingColumn = false
			}
		},
		onColumnDeleted(columnId) {
			this.showColumnModal = false
			this.confirmDeleteColumn(this.columns.find(c => c.id === columnId))
		},

		async openTask(item) {
			this.$store.consultasStore.commit('setCurrentTarea', { loading: true })
			const tarea = await getTareaByID({ task_id: item.task_id })
			if (tarea.code === 200) {
				let tareaData = tarea.task
				tareaData.prev_seen_by_me = item.task?.seen_by_me || null
				this.$store.consultasStore.commit('setCurrentTarea', tareaData)
			}
		}
	}
}
</script>

<style scoped>
.kanban-view {
	height: 100%;
}
.kanban-board {
	display: flex;
	flex-direction: row;
	gap: 16px;
	overflow-x: auto;
	padding-bottom: 16px;
	align-items: flex-start;
}

.ver-todas-container .v-input--selection-controls__ripple,
.ver-todas-container .v-input--selection-controls__input {
	transform: scale(0.95);
}
.ver-todas-container .caption {
	line-height: 1;
}
</style>
